import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import Button from "@/components/Button/Button";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import SimpleTable from "@/components/SimpleTable/SimpleTable";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import Pagination from "@/components/Table/Pagination";
import type { Column } from "@/types/common";
import eventService from "@/services/api/events";
import type { Event, EventParticipant } from "@/services/api/events";
import { EVENT_TYPE_LABELS, formatEventDate, getEventCityName } from "@/pages/Gatherings/lib/eventFormat";
import { formatINR } from "@/lib/currency";
import { downloadFile } from "@/lib/downloadExcel";
import toast from "@/lib/toast";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import routes from "@/routes/routes";
import styles from "./GatheringDetails.module.scss";

const PARTICIPANTS_PAGE_SIZE = 10;

const GUEST_COLUMNS: Column<EventParticipant>[] = [
  {
    name: "Guest",
    cell: (row) => (
      <div>
        <div className={styles.guestName}>{`${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "—"}</div>
        {row.companyName && <div className={styles.guestCompany}>{row.companyName}</div>}
      </div>
    ),
  },
  { name: "Tier", selector: (row) => row.subscriptionName || "—" },
  { name: "Payment", selector: (row) => formatINR(row.paymentPaid) },
];

const getVenueLine = (event: Event) => {
  const location = event.locations?.[0];
  return location?.address || location?.name || getEventCityName(event);
};

const GatheringDetails = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const numericEventId = eventId ? Number(eventId) : NaN;

  const [event, setEvent] = useState<Event | null>(null);
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [capacity, setCapacity] = useState<number | null>(null);
  const [filledCapacity, setFilledCapacity] = useState<number | null>(null);
  const [ticketRevenue, setTicketRevenue] = useState<number | null>(null);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [pageSelected, setPageSelected] = useState(1);

  const fetchEvent = useCallback(async () => {
    if (!Number.isFinite(numericEventId)) return;
    const result = await eventService.getEventById(numericEventId);
    setEvent(result?.data ?? null);
  }, [numericEventId]);

  const fetchParticipants = useCallback(async () => {
    if (!Number.isFinite(numericEventId)) return;
    setIsLoadingParticipants(true);
    setParticipantsError(null);
    try {
      const result = await eventService.getEventParticipants(numericEventId, {
        pageNo: pageSelected,
        perPage: PARTICIPANTS_PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
      });
      setParticipants(result?.data?.results ?? []);
      setTotalParticipants(result?.data?.totalRecord ?? 0);
      setCapacity(result?.data?.capacity ?? null);
      setFilledCapacity(result?.data?.filledCapacity ?? null);
      setTicketRevenue(result?.data?.ticketRevenue ?? null);
    } catch {
      setParticipantsError("Couldn't load the guest list.");
    } finally {
      setIsLoadingParticipants(false);
    }
  }, [numericEventId, debouncedSearch, pageSelected]);


  useEffect(() => {
    setPageSelected(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  
  const handleExport = async () => {
    if (!Number.isFinite(numericEventId)) return;
    try {
      const response = await eventService.exportEventParticipants(numericEventId, {
        search: debouncedSearch.trim() || undefined,
      });
      downloadFile({
        data: response.data,
        fileName: event?.name ? `${event.name}-guest-list` : "guest-list",
        extension: "xlsx",
      });
    } catch {
      toast.error("Failed to export guest list.");
    }
  };

  const stats: StatCardData[] = [
    {
      label: "Capacity",
      value: `${filledCapacity ?? 0} / ${capacity || "—"}`,
    },
    {
      label: "Ticket Revenue",
      value: formatINR(ticketRevenue ?? 0),
    },
  ];

  const headerSubtitle = event
    ? [EVENT_TYPE_LABELS[event.eventType] ?? event.eventType, formatEventDate(event.eventDate), getVenueLine(event)]
        .filter(Boolean)
        .join(" · ")
    : "";

  usePageTitle(event?.name ?? null);
  usePageSubtitle(headerSubtitle);

  const goToGatherings = () => navigate(routes.privateRoutes.GATHERINGS.path);

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={goToGatherings}>
        <ArrowIcon className={styles.backIcon} />
        Back to Gatherings
      </button>

      <StatCardsRow stats={stats} />

      <div className={styles.card}>
        <div className={styles.tabBar}>
          <span className={styles.tabActive}>
            Guest list
            <span className={styles.tabCount}>{totalParticipants}</span>
          </span>
        </div>

        <div className={styles.toolbarRow}>
          <CustomHeader
            search={search}
            setSearch={setSearch}
            searchPlaceholder="Search guests"
            onExport={handleExport}
            exportLabel="EXPORT LIST"
          />
        </div>

        {participantsError ? (
          <div className={styles.stateText}>
            <p>{participantsError}</p>
            <span className={styles.buttonWrap}>
              <Button outlined onClick={fetchParticipants}>
                TRY AGAIN
              </Button>
            </span>
          </div>
        ) : (
          <>
            <SimpleTable<EventParticipant>
              columns={GUEST_COLUMNS}
              data={participants}
              getRowKey={(row) => row.participantId}
              emptyMessage={isLoadingParticipants ? "Loading guest list…" : "No guests yet"}
            />
            <div className={styles.paginationWrapper}>
              <Pagination
                totalCount={totalParticipants}
                pageSelected={pageSelected}
                rowsPerPageValue={PARTICIPANTS_PAGE_SIZE}
                setPageSelected={setPageSelected}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GatheringDetails;
